import React, { useEffect } from 'react';
import { Navigate, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { inject, observer, Provider } from "mobx-react";
import { Toaster } from 'react-hot-toast';
import 'bootstrap';

import Utils from './utils/utils';
import AccessControl from "./utils/access_control";
import SignIn from './pages/signin';
import Settings from './pages/settings';
import Dashboard from './pages/dashboard';
import NewInvoice from './pages/new-invoice';
import NewCreditCardInvoice from './pages/new-cc-invoice/new-cc-invoice';
import Monitor from './pages/monitor';
import Cashflow from './pages/cashflow';
import Data from './pages/data';
import Workflow from './pages/workflow';
import AdminNavigationBar from "./components/admin-navigation-bar/index";
import AdminClients from "./pages/admin/admin-clients";
import AdminCcards from "./pages/admin/admin-ccards";
import AdminSuppliers from "./pages/admin/admin-suppliers";
import AdminGlNaturals from "./pages/admin/admin-gl-naturals";
import AdminUsers from "./pages/admin/admin-users";
import ScimProvisioning from "./pages/admin/scim-provisioning";
import AdminRoles from "./pages/admin/admin-roles";
import AdminOffices from "./pages/admin/admin-offices";
import Archives from './pages/archives';
import Footer from './components/footer';
import Menu from './components/menu';
import Reports from './pages/reports';
import BI from './pages/bi';
import Paiements from './pages/paiements';
import PaiementsApproval from './pages/paiements-approval';
import PlProcessingAccount from './pages/pl-processing-account';
import ExpensesAccount from './pages/expenses_account';
import NewCreditCardStatements from './pages/credit-card-statements/credit-card-statements.jsx';
import StatementAddPage from './pages/credit-card-statements/StatementAddPage';
import CreditCardTransactions from './pages/credit-card-transactions/credit-card-transactions.jsx';
import ExpensesAccountCreation from './pages/expenses_account_creation';
import NewExpenses from './pages/new-expenses';
import NewExpensesMobile from './pages/new-expenses-mobile';
import ExpensesImputations from "./pages/expenses_imputations";
import ExpensesRates from "./pages/expenses_rates";
import AdminNotifications from "./pages/admin/admin-notifications";
import ReviewTransaction from './pages/review-transaction';
import CreditCardCategories from "./pages/c-card-categories";
import ApprovalsPage from "./pages/approvals/approvals";

import { fetch } from './utils/api-wrapper';
import { savePreSigninLocation } from './utils/signin-redirect';
import ProtectedRoute from './utils/protected-route';
import ExpensesRules from "./pages/expenses_rules";
import AdminDepartments from "./pages/admin/admin-departments";
import ReviewExpense from './pages/review-expense';
import AdminTaxCodes from "./pages/admin/admin-tax-codes";
import OidcLogin from './pages/oidc-login/oidc-login.jsx';
import AdminMatters from "./pages/admin/admin-matters";
import FakeReceiptBuilder from './components/fake-receipt-builder/FakeReceiptBuilder.jsx';
import DataStore from './utils/data-store';
import { loadXpensCategories } from './utils/xpens-categories';

const App = inject("data")(observer(({ data }) => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tokenFromQuery = searchParams.get("token");
        const hasDirectEmailExpenseToken = !!(
            tokenFromQuery
            && location.pathname.startsWith('/expenses_account/')
            && searchParams.get("module") === "expense-exception-approval"
        );
        const hasDirectEmailApprovalToken = !!(
            tokenFromQuery
            && location.pathname === '/approvals'
            && searchParams.get("request_id")
        );

        if (hasDirectEmailExpenseToken || hasDirectEmailApprovalToken) {
            window.localStorage.setItem("jwt", tokenFromQuery);
        }

        if (window.localStorage.getItem("jwt") === null) {
            data.username = null;
            savePreSigninLocation(location);
            if (location.pathname === '/login') {
                navigate('/login', { replace: true });
            } else {
                navigate('/signin', { replace: true });
            }
            return;
        }
        // Always load data if role or roles_config is missing, even if token is still valid.
        // Aderant tenants: keep refreshing until config.aderant is in MobX (XPEN-2558 rank UI gate).
        const aderantConfigNotHydrated =
            Utils.isExportFormat(data, "aderant") && !Utils.hasAderantIntegration(data);
        if (
            data.config
            && !aderantConfigNotHydrated
            && data.expiration_date
            && data.role
            && data.roles_config
        ) {
            const expiration_date = new Date(data.expiration_date);
            const now = Date.now();
            const duree = (expiration_date - now) / (1000 * 60)
            if (duree > 30)
                return;
        }

        fetch("/refresh-token", "GET", {},
            response => {
                DataStore.updateUserData(data, response.data, response.data.token);

                if (data.password_expired && process.env.REACT_APP_OIDC_ENABLED !== 'true') {
                    navigate('/settings');
                } else if (location.pathname === "/signin" || location.pathname === "/") {
                    if (data.isMobile && Utils.userHasPageAccess(data, "expenses")) {
                        navigate('/expenses_accounts');
                    }
                    else {
                        AccessControl.redirectToDefaultLanding(data, navigate, "/dashboard");
                    }
                }
            },
            response => {
                savePreSigninLocation(location);
                window.localStorage.removeItem("jwt");
                navigate('/signin', { replace: true });
            }
        );
    }, [navigate, location.pathname, data]);

    // XPEN-3187: fetch the standard XPENS categories once per session, for
    // any authenticated user — loadXpensCategories no-ops if already cached.
    // Kept as its own effect, separate from the token-refresh effect above,
    // since the two are unrelated concerns. Depends on location.pathname like
    // the token-refresh effect so it re-fires on a client-side login redirect
    // (no full page reload), not just on first mount.
    useEffect(() => {
        if (window.localStorage.getItem("jwt") === null) return;
        loadXpensCategories(data);
    }, [location.pathname, data]);

    return (
        <div className="App">
            <Provider data={data}>
                <Main />
            </Provider>
            <Toaster />
        </div>
    );
}));

const Main = inject("data")(observer(({ data }) => {
    return (
        <div className="full-height">
            {
                data.username !== null && data.username !== "" && !data.hideMenu &&
                <Menu />
            }
            {(!data.isMobile || data.isMobile && !data.isMenuOpen)
                && <div className={data.username !== null && data.username !== "" && !data.hideMenu && !data.isMobile ? "offset-menu p-4" : ""}>
                    <Routes>
                        <Route exact path="/" element={process.env.REACT_APP_OIDC_ENABLED === 'true' ? <OidcLogin /> : <SignIn />} />
                        <Route path="/signin" element={process.env.REACT_APP_OIDC_ENABLED === 'true' ? <OidcLogin /> : <SignIn />} />
                        <Route path="/login" element={<SignIn />} />
                        {/* XPEN-3658: old email Approve/Refuse links (removed by XPEN-2570) land here. */}
                        <Route path="/webhooks" element={<Navigate to="/dashboard" replace />} />

                        <Route element={<ProtectedRoute/>}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            {AccessControl.canAccessApprovals(data) && <Route path="/approvals" element={<ApprovalsPage />} />}
                            {AccessControl.canAccessReports(data) && <Route path="/reports" element={<Reports />} />}
                            {AccessControl.userHasPageAccess(data, "bi") && <Route path="/bi" element={<BI />} />}
                            <Route path="/archives" element={<Archives />} />
                            {AccessControl.userHasPageAccess(data, "payments") && 
                                <>
                                    <Route path="/payments/invoices" element={<Paiements />} />
                                    <Route path="/payments/approval" element={<PaiementsApproval />} />
                                    <Route path="/payments/processing_account" element={<PlProcessingAccount />} />
                                </>
                            }
                            {AccessControl.userHasPageAccess(data, "expenses") && 
                                <>
                                    <Route path="/expenses_accounts" element={<ExpensesAccount />} />
                                    <Route path="/expenses_account/:account_id" element={<ExpensesAccountCreation />} />
                                    <Route path="/expenses_account/:account_id/add" element={<NewExpenses />} />
                                    <Route path="/expenses_account/:account_id/add-mobile" element={<NewExpensesMobile />} />
                                    {AccessControl.userHasPageOptionAccess(data, "expenses", "review") && <Route path="/expenses_account/:account_id/:checksum/review" element={<ReviewExpense />} />}
                                    {AccessControl.canAccessExpenseConfigurations(data) && <Route path="/expenses_configuration/imputations" element={<ExpensesImputations />} />}
                                    {AccessControl.canAccessExpenseRules(data) && <Route path="/expenses_rules" element={<ExpensesRules/>} />}
                                    {AccessControl.canAccessExpenseConfigurations(data) && <Route path="/expenses_configuration/rates" element={<ExpensesRates />} />}
                                </>
                            }
                            {AccessControl.userHasPageAccess(data, "c-card") && 
                                <>
                                    {AccessControl.hasActionAccess(data, "canAccessCreditCardModule") &&
                                        <>
                                            <Route path="/c_card/transactions" element={<CreditCardTransactions />} />
                                            <Route path="/c_card/statements/" element={<NewCreditCardStatements />} />
                                            <Route path="/c_card/statements/add" element={<StatementAddPage />} />
                                            <Route path="/c_card/statement/:statement_id" element={<CreditCardTransactions />} />
                                            <Route path="/c_card/statement/transaction/:statement_id/:transaction_checksum" element={<ReviewTransaction />} />
                                            <Route path="/c_card/configuration/categories" element={<CreditCardCategories />} />
                                            <Route path="/c_card/configuration/imputations" element={<ExpensesImputations isCreditCardImputations={true} />} />
                                        </>
                                    }
                                    {AccessControl.canCreateCreditCardInvoice(data) && <Route path="/new/credit-card" element={<NewCreditCardInvoice />} />}
                                </>
                            }

                            {AccessControl.userHasPageAccess(data, "monitor") && <Route path="/monitor" element={<Monitor />} />}
                            {AccessControl.userHasPageAccess(data, "cashflow") && <Route path="/cashflow" element={<Cashflow />} />}
                            {AccessControl.userHasPageAccess(data, "data") && <Route path="/data" element={<Data />} />}
                            {AccessControl.userHasPageAccess(data, "workflow") && <Route path="/workflow" element={<Workflow />} />}
                            {AccessControl.canAccessAdminShell(data) &&
                                <Route path="/admin" element={<AdminNavigationBar/>}>
                                    <Route index element={<Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="fake-receipt-builder" element={Utils.userHasRole(data, ['Admin', 'SuperAdmin']) ? <FakeReceiptBuilder /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="clients" element={AccessControl.canAccessAdminSection(data, "clients") ? <AdminClients /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="suppliers" element={AccessControl.canAccessAdminSection(data, "suppliers") ? <AdminSuppliers /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="gl_naturals" element={AccessControl.canAccessAdminSection(data, "gl_codes") ? <AdminGlNaturals /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="users" element={AccessControl.canAccessAdminSection(data, "users") ? <AdminUsers /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="users/scim/:clientId" element={AccessControl.canAccessAdminSection(data, "users") ? <ScimProvisioning /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="roles" element={AccessControl.canAccessAdminSection(data, "roles") ? <AdminRoles /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="offices" element={AccessControl.canAccessAdminSection(data, "offices") ? <AdminOffices /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="departments" element={AccessControl.canAccessAdminSection(data, "departments") ? <AdminDepartments /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="notifications" element={AccessControl.canAccessAdminSection(data, "notifications") ? <AdminNotifications /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="tax-codes" element={AccessControl.canAccessAdminSection(data, "tax_codes") ? <AdminTaxCodes /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="matters" element={AccessControl.canAccessAdminSection(data, "matters") ? <AdminMatters /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                    <Route path="ccards" element={AccessControl.canAccessAdminSection(data, "credit_cards") ? <AdminCcards /> : <Navigate to={AccessControl.getFirstAdminSectionPath(data)} replace />} />
                                </Route>
                            }
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/new" element={<NewInvoice />} />
                        </Route>

                    </Routes>
                </div>
            }
            <Footer />
        </div>
    );
}));

export default App;
